/**
 * Cookie utility functions for managing browser cookies
 * Used to synchronize authentication between legacy (logic.js) and new React app
 */

/**
 * Set a cookie with the given name, value, and expiration days
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Number of days until expiration (default: 30)
 * @param path - Cookie path (default: '/')
 * @param domain - Cookie domain (optional, e.g., '.mafiaworldtour.org')
 */
export function setCookie(name: string, value: string, days: number = 30, path: string = '/', domain?: string): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  let cookieString = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=${path}`;

  if (domain) {
    cookieString += `;domain=${domain}`;
  }

  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1, c.length);
    }
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }

  return null;
}

/**
 * Delete a cookie by name
 * @param name - Cookie name
 * @param path - Cookie path (default: '/')
 * @param domain - Cookie domain (optional, e.g., '.mafiaworldtour.org')
 */
export function deleteCookie(name: string, path: string = '/', domain?: string): void {
  let cookieString = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path}`;

  if (domain) {
    cookieString += `;domain=${domain}`;
  }

  document.cookie = cookieString;
}

/**
 * Check if a cookie exists
 * @param name - Cookie name
 * @returns true if cookie exists, false otherwise
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}
